import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { docUrlForPath, openSupport } from '../support';
import Wordmark from './Wordmark';

/**
 * The app's own footer, shown under every page.
 *
 * Rendered once in App.jsx after <Routes>, inside <Page>, so it inherits the
 * page's width and padding and sits below whatever the route drew. Adding it
 * per-page would mean thirty-odd call sites and one of them would always be
 * missed.
 *
 * The wordmark itself lives in <Wordmark /> - the page headers draw the same
 * mark, and two copies would drift apart.
 *
 * The two links underneath ARE translated, because they are labels, not a name.
 */
export default function AppFooter() {
  const { t } = useTranslation();

  // Resolves to the documentation for THIS page. Each page used to carry its own
  // "visit our Knowledge Base" link; they were dropped so a screen offers one,
  // and this keeps it specific rather than always landing on the setup guide.
  const { pathname } = useLocation();

  // font-[inherit] and an explicit size are load-bearing, not tidiness: one of these
  // two controls is a <button>, and a button does NOT inherit font-family or
  // font-size from its parent - it takes the browser's own button defaults. Without
  // these the two links sit side by side in visibly different fonts. (Plain
  // `font-inherit` is not a Tailwind utility and silently generates nothing; the
  // arbitrary-value form is.)
  const linkClass =
    'font-[inherit] text-[13px] leading-none text-gray-500 hover:text-gray-700 ' +
    'no-underline hover:underline underline-offset-2 ' +
    'focus-visible:outline-2 focus-visible:outline-offset-2 rounded-sm bg-transparent border-0 ' +
    'p-0 cursor-pointer';

  return (
    <footer className="w-full mt-10 mb-6 flex flex-col items-center gap-1.5 text-center">
      <Wordmark />

      <div className="text-[13px] flex items-center gap-2">
        {/* A real <a href> so the link is middle-clickable and shows its target in
            the status bar. target="_blank" keeps the docs out of the app frame. */}
        <a
          href={docUrlForPath(pathname)}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          {t('footer.userGuide')}
        </a>
        <span aria-hidden="true" className="text-gray-300">|</span>
        <button type="button" onClick={openSupport} className={linkClass}>
          {t('footer.contactSupport')}
        </button>
      </div>
    </footer>
  );
}
